// N-segment proficiency pill sized by per-band counts — promoted from analyse
// on its second consumer (gradebook). Colors from the level's inverted slot
// index or authored color.
import { useMemo, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import {
  slotColor,
  slotForIndex,
  type GradingScaleLevel,
} from '@/components/ap/proficiency/proficiencyPalette';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface ProficiencyDistributionPillBarProps {
  distribution: Record<string, number> | undefined;
  gradingScaleLevels?: ReadonlyArray<GradingScaleLevel>;
}

export const ProficiencyDistributionPillBar = ({
  distribution,
  gradingScaleLevels,
}: ProficiencyDistributionPillBarProps) => {
  const { t } = useTranslation();

  const safeDistribution = useMemo(() => distribution ?? {}, [distribution]);
  const levels = useMemo(() => gradingScaleLevels ?? [], [gradingScaleLevels]);

  const total = useMemo(
    () => levels.reduce((acc, level) => acc + (safeDistribution[level.label] ?? 0), 0),
    [levels, safeDistribution]
  );

  const segmentColor = (level: GradingScaleLevel, index: number): string => {
    const authored = level.color?.trim();
    if (authored) return authored;
    return slotColor(slotForIndex(Math.max(0, levels.length - 1 - index), 'summary'));
  };

  const tooltipBody = (
    <div className="flex flex-col gap-1">
      {levels.map((level, index) => (
        <div key={level.label} className="flex items-center gap-2">
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: segmentColor(level, index) }}
            aria-hidden
          />
          <span>{level.label}</span>
          <span className="ms-auto tabular-nums">{safeDistribution[level.label] ?? 0}</span>
        </div>
      ))}
    </div>
  );

  if (levels.length === 0) {
    return (
      <span
        className="text-muted-foreground"
        aria-label={t('AnalyseV3Compare.proficiency_distribution')}
      >
        —
      </span>
    );
  }

  if (total === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="text-muted-foreground"
            aria-label={t('AnalyseV3Compare.proficiency_distribution')}
          >
            —
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltipBody}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex h-3 w-full min-w-24 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={t('AnalyseV3Compare.proficiency_distribution')}
        >
          {levels.map((level, index) => {
            const count = safeDistribution[level.label] ?? 0;
            if (count === 0) return null;
            const style: CSSProperties = {
              width: `${(count / total) * 100}%`,
              backgroundColor: segmentColor(level, index),
            };
            return <span key={level.label} style={style} data-band={level.label} />;
          })}
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltipBody}</TooltipContent>
    </Tooltip>
  );
};
